import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  useInView,
  useAnimationFrame,
  useReducedMotion,
} from "framer-motion";
import { cn } from "../../lib/utils";
import { Sparkles, Volume2, VolumeX, ArrowDown } from "lucide-react";

// ═══════════════════════════════════════════════════════════════════════════
// RENAISSANCE LANDING PAGE - Sacred Royal Aesthetic
// De-gamified "Antique Gold" palette with cosmic textures
// ═══════════════════════════════════════════════════════════════════════════

// Sacred Color Palette
const COLORS = {
  antiqueGold: "#D4AF37",
  antiqueGoldLight: "#E8C872",
  antiqueGoldDark: "#B8962E",
  vedicCrimson: "#8B1538",
  vedicCrimsonLight: "#A91B45",
  cosmicIndigo: "#1e1b4b",
  cosmicViolet: "#2e1065",
  stoneLight: "#f5f5f4", // stone-100
  stoneMuted: "#a8a29e", // stone-400
};

// Sanskrit characters for floating effect
const SANSKRIT_CHARS = [
  "ॐ",
  "ऋ",
  "अ",
  "इ",
  "उ",
  "श",
  "स",
  "व",
  "ह",
  "त",
  "न",
  "म",
  "य",
  "र",
  "ल",
];

interface MandalaNode {
  id: number;
  name: string;
  subtitle: string;
  x: number;
  y: number;
  size: number;
  hymns: number;
}

// Constellation data - positioned for star map layout
const constellationData: MandalaNode[] = [
  {
    id: 1,
    name: "The Gateway",
    subtitle: "Agni & Divine Invocations",
    x: 15,
    y: 25,
    size: 1.4,
    hymns: 191,
  },
  {
    id: 2,
    name: "Cosmic Order",
    subtitle: "Hymns of Gritsamada",
    x: 35,
    y: 15,
    size: 1.0,
    hymns: 43,
  },
  {
    id: 3,
    name: "Sacred Fire",
    subtitle: "Vishvamitra's Wisdom",
    x: 55,
    y: 22,
    size: 1.1,
    hymns: 62,
  },
  {
    id: 4,
    name: "Divine Light",
    subtitle: "Vamadeva's Visions",
    x: 75,
    y: 18,
    size: 1.0,
    hymns: 58,
  },
  {
    id: 5,
    name: "The Elements",
    subtitle: "Atri's Revelations",
    x: 88,
    y: 35,
    size: 1.15,
    hymns: 87,
  },
  {
    id: 6,
    name: "Thunder & Rain",
    subtitle: "Bharadvaja's Prayers",
    x: 78,
    y: 55,
    size: 1.1,
    hymns: 75,
  },
  {
    id: 7,
    name: "Celestial Waters",
    subtitle: "Vasishtha's Hymns",
    x: 58,
    y: 65,
    size: 1.2,
    hymns: 104,
  },
  {
    id: 8,
    name: "The Offering",
    subtitle: "Kanva Family Songs",
    x: 38,
    y: 58,
    size: 1.15,
    hymns: 103,
  },
  {
    id: 9,
    name: "Soma Pavamana",
    subtitle: "The Sacred Elixir",
    x: 20,
    y: 50,
    size: 1.3,
    hymns: 114,
  },
  {
    id: 10,
    name: "The Mysteries",
    subtitle: "Creation & Beyond",
    x: 50,
    y: 42,
    size: 1.5,
    hymns: 191,
  },
];

// Connection paths between stars (constellation lines)
const connectionPaths = [
  [1, 2],
  [2, 3],
  [3, 4],
  [4, 5],
  [5, 6],
  [6, 7],
  [7, 8],
  [8, 9],
  [9, 1],
  [10, 1],
  [10, 3],
  [10, 5],
  [10, 7],
  [10, 9],
  [2, 10],
  [4, 10],
  [6, 10],
  [8, 10],
];

// ═══════════════════════════════════════════════════════════════════════════
// SANSKRIT ECHOES - Floating characters with magnetic repulsion
// ═══════════════════════════════════════════════════════════════════════════
interface SanskritParticle {
  id: number;
  char: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  opacity: number;
  rotation: number;
}

const SanskritEchoes: React.FC<{ mouseX: number; mouseY: number }> = ({
  mouseX,
  mouseY,
}) => {
  const prefersReducedMotion = useReducedMotion();
  const [particles, setParticles] = useState<SanskritParticle[]>([]);
  const frameRef = useRef(0);

  // Initialize particles
  useEffect(() => {
    const initialParticles: SanskritParticle[] = Array.from(
      { length: 30 },
      (_, i) => ({
        id: i,
        char: SANSKRIT_CHARS[Math.floor(Math.random() * SANSKRIT_CHARS.length)],
        x: Math.random() * 100,
        y: Math.random() * 100,
        vx: (Math.random() - 0.5) * 0.02,
        vy: (Math.random() - 0.5) * 0.02,
        size: 12 + Math.random() * 24,
        opacity: 0.03 + Math.random() * 0.05,
        rotation: Math.random() * 360,
      }),
    );
    setParticles(initialParticles);
  }, []);

  // Animate particles with magnetic repulsion — disabled for reduced motion
  useAnimationFrame(() => {
    if (prefersReducedMotion) return;
    frameRef.current++;
    if (frameRef.current % 2 !== 0) return; // Throttle to 30fps

    setParticles((prev) =>
      prev.map((p) => {
        // Calculate distance from mouse (mouse coords are 0-100)
        const dx = p.x - mouseX;
        const dy = p.y - mouseY;
        const dist = Math.sqrt(dx * dx + dy * dy);

        // Magnetic repulsion if within range
        const repulsionRadius = 15;
        let newVx = p.vx;
        let newVy = p.vy;

        if (dist < repulsionRadius && dist > 0) {
          const force = ((repulsionRadius - dist) / repulsionRadius) * 0.3;
          newVx += (dx / dist) * force;
          newVy += (dy / dist) * force;
        }

        // Apply friction
        newVx *= 0.98;
        newVy *= 0.98;

        // Add slight drift
        newVx += (Math.random() - 0.5) * 0.005;
        newVy += (Math.random() - 0.5) * 0.005;

        // Update position with wrapping
        let newX = p.x + newVx;
        let newY = p.y + newVy;

        if (newX < -5) newX = 105;
        if (newX > 105) newX = -5;
        if (newY < -5) newY = 105;
        if (newY > 105) newY = -5;

        return {
          ...p,
          x: newX,
          y: newY,
          vx: newVx,
          vy: newVy,
          rotation: p.rotation + 0.1,
        };
      }),
    );
  });

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-[5]">
      {particles.map((p) => (
        <div
          key={p.id}
          className="absolute font-sanskrit text-stone-100 select-none"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            fontSize: `${p.size}px`,
            opacity: p.opacity,
            transform: `rotate(${p.rotation}deg)`,
            textShadow: `0 0 20px ${COLORS.antiqueGold}40`,
          }}
        >
          {p.char}
        </div>
      ))}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// NEBULA GRADIENT - Breathing cosmic background
// ═══════════════════════════════════════════════════════════════════════════
const NebulaGradient: React.FC = () => {
  const prefersReducedMotion = useReducedMotion();
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-[1]">
      {/* Primary Nebula - Deep Indigo */}
      <motion.div
        className="absolute w-[150vw] h-[150vh] rounded-full blur-[200px]"
        style={{
          background: `radial-gradient(circle, ${COLORS.cosmicIndigo}40 0%, transparent 70%)`,
          left: "50%",
          top: "50%",
          x: "-50%",
          y: "-50%",
        }}
        animate={
          prefersReducedMotion
            ? { scale: 1, opacity: 0.4 }
            : { scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }
        }
        transition={
          prefersReducedMotion
            ? undefined
            : { duration: 15, repeat: Infinity, ease: "easeInOut" }
        }
      />

      {/* Secondary Nebula - Violet accent */}
      <motion.div
        className="absolute w-[100vw] h-[100vh] rounded-full blur-[150px]"
        style={{
          background: `radial-gradient(circle, ${COLORS.cosmicViolet}30 0%, transparent 60%)`,
          left: "30%",
          top: "30%",
        }}
        animate={
          prefersReducedMotion
            ? { x: 0, y: 0, scale: 1, opacity: 0.28 }
            : {
                x: [0, 50, 0],
                y: [0, -30, 0],
                scale: [1, 1.2, 1],
                opacity: [0.2, 0.35, 0.2],
              }
        }
        transition={
          prefersReducedMotion
            ? undefined
            : { duration: 20, repeat: Infinity, ease: "easeInOut" }
        }
      />

      {/* Tertiary Nebula - Subtle crimson warmth */}
      <motion.div
        className="absolute w-[80vw] h-[80vh] rounded-full blur-[180px]"
        style={{
          background: `radial-gradient(circle, ${COLORS.vedicCrimson}15 0%, transparent 50%)`,
          right: "10%",
          bottom: "20%",
        }}
        animate={
          prefersReducedMotion
            ? { scale: 1, opacity: 0.2 }
            : { scale: [1, 1.15, 1], opacity: [0.15, 0.25, 0.15] }
        }
        transition={
          prefersReducedMotion
            ? undefined
            : { duration: 18, repeat: Infinity, ease: "easeInOut", delay: 5 }
        }
      />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// GLASS RELIC BUTTON - Sacred entry point
// ═══════════════════════════════════════════════════════════════════════════
const GlassRelicButton: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
}> = ({ onClick, children }) => {
  return (
    <button
      onClick={onClick}
      className={cn(
        "group relative px-12 py-5 rounded-full",
        "bg-transparent backdrop-blur-md",
        "border border-[#D4AF37]/40",
        "text-stone-100 font-medium text-lg tracking-widest uppercase",
        "transition-all duration-700 ease-out",
        "hover:border-[#D4AF37]/80",
        "hover:shadow-[0_0_60px_rgba(212,175,55,0.15),inset_0_0_30px_rgba(212,175,55,0.05)]",
        "focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/30 focus:ring-offset-4 focus:ring-offset-zinc-950",
      )}
      style={{ fontFamily: "Cinzel, serif" }}
    >
      {/* Inner glow layer */}
      <div className="absolute inset-0 rounded-full bg-gradient-to-b from-[#D4AF37]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

      {/* Metallic border gradient */}
      <div
        className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700"
        style={{
          background: `linear-gradient(135deg, ${COLORS.antiqueGoldLight}20 0%, transparent 50%, ${COLORS.antiqueGoldDark}20 100%)`,
        }}
      />

      {/* Animated border glow */}
      <motion.div
        className="absolute -inset-[1px] rounded-full opacity-0 group-hover:opacity-100"
        style={{
          background: `conic-gradient(from 0deg, ${COLORS.antiqueGold}00, ${COLORS.antiqueGold}40, ${COLORS.antiqueGold}00)`,
        }}
        animate={{
          rotate: 360,
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "linear",
        }}
      />

      <span className="relative z-10" style={{ color: COLORS.antiqueGold }}>
        {children}
      </span>
    </button>
  );
};

interface RenaissanceLandingProps {
  onEnterMandala: (mandalaNumber: number) => void;
}

export const RenaissanceLanding: React.FC<RenaissanceLandingProps> = ({
  onEnterMandala,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const [hoveredNode, setHoveredNode] = useState<MandalaNode | null>(null);
  const [isMuted, setIsMuted] = useState(true);

  const prefersReducedMotion = useReducedMotion();

  // Global mouse tracking for Sanskrit Echoes
  const [globalMouse, setGlobalMouse] = useState({ x: 50, y: 50 });

  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      setGlobalMouse({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100,
      });
    };
    window.addEventListener("mousemove", handleGlobalMouseMove);
    return () => window.removeEventListener("mousemove", handleGlobalMouseMove);
  }, []);

  // Scroll progress
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  // Smooth spring for parallax — higher stiffness for snappier scroll
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 200,
    damping: 40,
  });

  // Parallax transforms
  const heroOpacity = useTransform(smoothProgress, [0, 0.2], [1, 0]);
  const heroScale = useTransform(smoothProgress, [0, 0.3], [1, 0.8]);
  const titleY = useTransform(smoothProgress, [0, 0.3], [0, -200]);

  // 3D Tilt effect for hero
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!heroRef.current) return;
    const rect = heroRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setMousePosition({ x: x * 100 + 50, y: y * 100 + 50 });
  }, []);

  const tiltX = useSpring(mousePosition.y * 0.1 - 5, {
    stiffness: 150,
    damping: 20,
  });
  const tiltY = useSpring(mousePosition.x * -0.1 + 5, {
    stiffness: 150,
    damping: 20,
  });

  return (
    <div
      ref={containerRef}
      className="relative bg-zinc-950 text-stone-100 overflow-x-hidden"
    >
      {/* ═══════════════════════════════════════════════════════════════
          COSMIC CANVAS LAYERS
          ═══════════════════════════════════════════════════════════════ */}

      {/* Layer 1: Nebula Gradient (Breathing) */}
      <NebulaGradient />

      {/* Layer 2: Noise Texture Overlay (Film Grain) */}
      <div
        className="fixed inset-0 pointer-events-none opacity-[0.03] z-[2]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.7' numOctaves='5' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Layer 3: Sanskrit Echoes */}
      <SanskritEchoes mouseX={globalMouse.x} mouseY={globalMouse.y} />

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 1: THE COSMIC VOID (Hero)
          ═══════════════════════════════════════════════════════════════ */}
      <motion.section
        ref={heroRef}
        className="relative h-screen flex items-center justify-center overflow-hidden z-10"
        onMouseMove={handleMouseMove}
        style={{ opacity: heroOpacity, scale: heroScale }}
      >
        {/* Radial Gradient - Antique Gold glow */}
        <div className="absolute inset-0">
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px]"
            style={{
              background: `radial-gradient(circle, ${COLORS.antiqueGold}08 0%, transparent 60%)`,
            }}
          />
        </div>

        {/* Background Title "VEDA" - Behind the figure */}
        <motion.div
          className="absolute inset-0 flex items-center justify-center pointer-events-none select-none"
          style={{ y: titleY }}
        >
          <h1
            className="text-[20rem] md:text-[28rem] lg:text-[35rem] font-black tracking-tighter leading-none"
            style={{
              fontFamily: "Cinzel, serif",
              opacity: 0.03,
              color: COLORS.stoneLight,
            }}
          >
            VEDA
          </h1>
        </motion.div>

        {/* Central Figure - Wireframe Deity with 3D Tilt */}
        <motion.div
          className="relative z-10 w-[500px] h-[600px] md:w-[600px] md:h-[720px]"
          style={{
            rotateX: tiltX,
            rotateY: tiltY,
            transformStyle: "preserve-3d",
            perspective: 1000,
          }}
        >
          {/* Divine Glow Behind Figure - Antique Gold */}
          <div
            className="absolute inset-0 blur-3xl"
            style={{
              background: `radial-gradient(circle, ${COLORS.antiqueGold}15 0%, ${COLORS.vedicCrimson}05 50%, transparent 70%)`,
            }}
          />

          {/* Wireframe Mandala SVG */}
          <svg
            className="w-full h-full"
            viewBox="0 0 400 480"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Outer circles - Metallic gradient */}
            <circle
              cx="200"
              cy="240"
              r="180"
              stroke="url(#sacredGoldGradient)"
              strokeWidth="0.5"
              opacity="0.25"
            />
            <circle
              cx="200"
              cy="240"
              r="150"
              stroke="url(#sacredGoldGradient)"
              strokeWidth="0.5"
              opacity="0.2"
            />
            <circle
              cx="200"
              cy="240"
              r="120"
              stroke="url(#sacredGoldGradient)"
              strokeWidth="0.5"
              opacity="0.15"
            />
            <circle
              cx="200"
              cy="240"
              r="90"
              stroke="url(#sacredGoldGradient)"
              strokeWidth="0.5"
              opacity="0.1"
            />

            {/* Radiating lines - Subtle metallic */}
            {Array.from({ length: 24 }).map((_, i) => {
              const angle = (i * 15 * Math.PI) / 180;
              const x1 = 200 + Math.cos(angle) * 60;
              const y1 = 240 + Math.sin(angle) * 60;
              const x2 = 200 + Math.cos(angle) * 180;
              const y2 = 240 + Math.sin(angle) * 180;
              return (
                <line
                  key={i}
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke="url(#sacredGoldGradient)"
                  strokeWidth="0.3"
                  opacity={0.08 + (i % 3) * 0.03}
                />
              );
            })}

            {/* Central lotus pattern */}
            {Array.from({ length: 8 }).map((_, i) => {
              const angle = (i * 45 * Math.PI) / 180;
              const x = 200 + Math.cos(angle) * 40;
              const y = 240 + Math.sin(angle) * 40;
              return (
                <ellipse
                  key={i}
                  cx={x}
                  cy={y}
                  rx="25"
                  ry="12"
                  fill="none"
                  stroke="url(#sacredGoldGradient)"
                  strokeWidth="0.5"
                  opacity="0.3"
                  transform={`rotate(${i * 45}, ${x}, ${y})`}
                />
              );
            })}

            {/* Center Om symbol */}
            <circle
              cx="200"
              cy="240"
              r="30"
              fill="none"
              stroke="url(#sacredGoldGradient)"
              strokeWidth="1"
              opacity="0.4"
            />
            <text
              x="200"
              y="250"
              textAnchor="middle"
              className="font-sanskrit"
              fill={COLORS.antiqueGold}
              fontSize="36"
              opacity="0.7"
            >
              ॐ
            </text>

            {/* Pulsing inner glow */}
            <circle
              cx="200"
              cy="240"
              r="50"
              fill="url(#sacredCenterGlow)"
              opacity="0.3"
            >
              {!prefersReducedMotion && (
                <>
                  <animate
                    attributeName="r"
                    values="45;55;45"
                    dur="6s"
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="opacity"
                    values="0.2;0.4;0.2"
                    dur="6s"
                    repeatCount="indefinite"
                  />
                </>
              )}
            </circle>

            {/* Sacred Gradient definitions */}
            <defs>
              <linearGradient
                id="sacredGoldGradient"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop
                  offset="0%"
                  stopColor={COLORS.antiqueGoldLight}
                  stopOpacity="0.8"
                />
                <stop
                  offset="50%"
                  stopColor={COLORS.antiqueGold}
                  stopOpacity="0.6"
                />
                <stop
                  offset="100%"
                  stopColor={COLORS.antiqueGoldDark}
                  stopOpacity="0.4"
                />
              </linearGradient>
              <radialGradient id="sacredCenterGlow" cx="50%" cy="50%" r="50%">
                <stop
                  offset="0%"
                  stopColor={COLORS.antiqueGold}
                  stopOpacity="0.4"
                />
                <stop
                  offset="100%"
                  stopColor={COLORS.antiqueGold}
                  stopOpacity="0"
                />
              </radialGradient>
            </defs>
          </svg>

          {/* Radial fade mask */}
          <div className="absolute inset-0 bg-gradient-radial from-transparent via-transparent to-zinc-950" />
        </motion.div>

        {/* Foreground Title */}
        <div className="absolute inset-0 flex flex-col items-center justify-center z-20 pointer-events-none">
          {/* Subtitle above */}
          <motion.p
            className="text-xs tracking-[0.5em] uppercase mb-8"
            style={{
              fontFamily: "JetBrains Mono, monospace",
              color: `${COLORS.antiqueGold}cc`,
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            The Code of the Cosmos
          </motion.p>

          {/* Main Title - Mix blend for visibility */}
          <motion.h1
            className="text-7xl md:text-8xl lg:text-9xl font-black tracking-tighter mix-blend-exclusion"
            style={{ fontFamily: "Cinzel, serif" }}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            VEDA
          </motion.h1>

          {/* Tagline below */}
          <motion.p
            className="text-sm md:text-base mt-6 tracking-wide"
            style={{ color: COLORS.stoneMuted }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            4,000 Years of Sacred Wisdom
          </motion.p>

          {/* Hero CTA — above the fold */}
          <motion.div
            className="mt-12 pointer-events-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.6 }}
          >
            <GlassRelicButton onClick={() => onEnterMandala(1)}>
              Enter the Vedas
            </GlassRelicButton>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20"
          animate={prefersReducedMotion ? { y: 0 } : { y: [0, 10, 0] }}
          transition={
            prefersReducedMotion
              ? undefined
              : { repeat: Infinity, duration: 2.5 }
          }
        >
          <div
            className="flex flex-col items-center gap-2"
            style={{ color: `${COLORS.stoneMuted}80` }}
          >
            <span
              className="text-[11px] tracking-[0.3em] uppercase"
              style={{ fontFamily: "JetBrains Mono, monospace" }}
            >
              Scroll to Explore
            </span>
            <ArrowDown className="w-4 h-4" />
          </div>
        </motion.div>

        {/* Sound Toggle */}
        <button
          onClick={() => setIsMuted(!isMuted)}
          aria-label={isMuted ? "Unmute ambient sound" : "Mute ambient sound"}
          className="absolute top-6 right-6 z-30 p-3 rounded-full bg-zinc-900/30 backdrop-blur-sm border border-zinc-800/50 hover:border-[#D4AF37]/30 transition-colors duration-500"
        >
          {isMuted ? (
            <VolumeX className="w-4 h-4" style={{ color: COLORS.stoneMuted }} />
          ) : (
            <Volume2
              className="w-4 h-4"
              style={{ color: COLORS.antiqueGold }}
            />
          )}
        </button>
      </motion.section>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 2: THE CONSTELLATION (Star Map Navigation)
          ═══════════════════════════════════════════════════════════════ */}
      <section className="relative min-h-screen py-20 z-10">
        {/* Section Title */}
        <div className="text-center mb-16">
          <EditorialReveal>
            <p
              className="text-xs tracking-[0.5em] uppercase mb-4"
              style={{
                fontFamily: "JetBrains Mono, monospace",
                color: `${COLORS.antiqueGold}b3`,
              }}
            >
              Navigate the Sacred Texts
            </p>
          </EditorialReveal>
          <EditorialReveal delay={0.2}>
            <h2
              className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-stone-100"
              style={{ fontFamily: "Cinzel, serif" }}
            >
              The Ten Mandalas
            </h2>
          </EditorialReveal>
        </div>

        {/* Star Map */}
        <div className="relative w-full max-w-6xl mx-auto h-[600px] md:h-[700px]">
          {/* Connection Lines SVG */}
          <svg
            className="absolute inset-0 w-full h-full"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            {connectionPaths.map(([fromId, toId], index) => {
              const from = constellationData.find((n) => n.id === fromId)!;
              const to = constellationData.find((n) => n.id === toId)!;
              return (
                <motion.path
                  key={index}
                  d={`M ${from.x} ${from.y} L ${to.x} ${to.y}`}
                  stroke="url(#sacredLineGradient)"
                  strokeWidth="0.12"
                  fill="none"
                  initial={{ pathLength: 0, opacity: 0 }}
                  whileInView={{ pathLength: 1, opacity: 0.3 }}
                  transition={{ duration: 1.5, delay: index * 0.05 }}
                  viewport={{ once: true }}
                />
              );
            })}
            <defs>
              <linearGradient
                id="sacredLineGradient"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop
                  offset="0%"
                  stopColor={COLORS.antiqueGold}
                  stopOpacity="0.25"
                />
                <stop
                  offset="100%"
                  stopColor={COLORS.antiqueGold}
                  stopOpacity="0.05"
                />
              </linearGradient>
            </defs>
          </svg>

          {/* Star Nodes */}
          {constellationData.map((node, index) => (
            <motion.button
              key={node.id}
              className={cn(
                "absolute transform -translate-x-1/2 -translate-y-1/2 group",
                "focus:outline-none focus:ring-2 focus:ring-offset-4 focus:ring-offset-zinc-950 rounded-full",
              )}
              style={{
                left: `${node.x}%`,
                top: `${node.y}%`,
                // @ts-ignore - custom property for focus ring
                "--tw-ring-color": `${COLORS.antiqueGold}40`,
              }}
              initial={{ scale: 0, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              transition={{ delay: index * 0.1, type: "spring" }}
              viewport={{ once: true }}
              aria-label={`Mandala ${node.id}: ${node.name} — ${node.hymns} hymns`}
              onMouseEnter={() => setHoveredNode(node)}
              onMouseLeave={() => setHoveredNode(null)}
              onClick={() => onEnterMandala(node.id)}
            >
              {/* Glow effect - Vedic Crimson on hover */}
              <div
                className={cn(
                  "absolute inset-0 rounded-full blur-xl transition-all duration-700 pointer-events-none",
                )}
                style={{
                  transform: `scale(${node.size * 3})`,
                  background:
                    node.id === 10 ? `${COLORS.antiqueGold}15` : "transparent",
                }}
              />
              <div
                className="absolute inset-0 rounded-full blur-xl transition-all duration-700 pointer-events-none opacity-0 group-hover:opacity-100"
                style={{
                  transform: `scale(${node.size * 3})`,
                  background: `${COLORS.vedicCrimson}30`,
                }}
              />

              {/* Outer ring */}
              <div
                className={cn(
                  "relative rounded-full border transition-all duration-500",
                  "bg-zinc-900/30 backdrop-blur-sm",
                )}
                style={{
                  width: `${node.size * 60}px`,
                  height: `${node.size * 60}px`,
                  borderColor:
                    node.id === 10
                      ? `${COLORS.antiqueGold}50`
                      : `${COLORS.antiqueGold}20`,
                }}
              >
                {/* Inner star - Metallic gold gradient */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div
                    className="rounded-full transition-all duration-500 group-hover:shadow-[0_0_30px_rgba(212,175,55,0.4)]"
                    style={{
                      width: `${node.size * 16}px`,
                      height: `${node.size * 16}px`,
                      background: `linear-gradient(135deg, ${COLORS.antiqueGoldLight} 0%, ${COLORS.antiqueGold} 50%, ${COLORS.antiqueGoldDark} 100%)`,
                    }}
                  />
                </div>

                {/* Pulse animation for central node */}
                {node.id === 10 && (
                  <div
                    className="absolute inset-0 rounded-full animate-ping"
                    style={{
                      borderWidth: "1px",
                      borderColor: `${COLORS.antiqueGold}30`,
                    }}
                  />
                )}
              </div>

              {/* Node label */}
              <div
                className={cn(
                  "absolute left-1/2 -translate-x-1/2 whitespace-nowrap transition-all duration-300",
                  "opacity-0 group-hover:opacity-100 -translate-y-2 group-hover:translate-y-0",
                  node.y > 50 ? "bottom-full mb-3" : "top-full mt-3",
                )}
              >
                <span
                  className="text-[11px] tracking-[0.2em] uppercase"
                  style={{
                    fontFamily: "JetBrains Mono, monospace",
                    color: `${COLORS.antiqueGold}cc`,
                  }}
                >
                  {String(node.id).padStart(2, "0")}
                </span>
              </div>
            </motion.button>
          ))}

          {/* Floating Tooltip — viewport-aware positioning */}
          {hoveredNode && (
            <motion.div
              className={cn(
                "absolute z-50 pointer-events-none",
                "bg-zinc-900/90 backdrop-blur-xl",
                "rounded-xl",
                "px-6 py-4",
                "w-[220px]",
              )}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              style={{
                ...(hoveredNode.x > 55
                  ? { right: `${100 - hoveredNode.x + 4}%` }
                  : { left: `${hoveredNode.x + 4}%` }),
                ...(hoveredNode.y > 55
                  ? { bottom: `${100 - hoveredNode.y + 4}%` }
                  : { top: `${hoveredNode.y + 4}%` }),
                borderWidth: "1px",
                borderColor: `${COLORS.antiqueGold}30`,
                boxShadow: `0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 30px ${COLORS.antiqueGold}10`,
              }}
            >
              <p
                className="text-[11px] tracking-[0.3em] uppercase mb-1"
                style={{
                  fontFamily: "JetBrains Mono, monospace",
                  color: `${COLORS.antiqueGold}cc`,
                }}
              >
                Mandala {String(hoveredNode.id).padStart(2, "0")}
              </p>
              <h3
                className="text-xl font-semibold text-stone-100 mb-1"
                style={{ fontFamily: "Cinzel, serif" }}
              >
                {hoveredNode.name}
              </h3>
              <p className="text-sm mb-2" style={{ color: COLORS.stoneMuted }}>
                {hoveredNode.subtitle}
              </p>
              <p
                className="text-xs"
                style={{ color: `${COLORS.antiqueGold}aa` }}
              >
                {hoveredNode.hymns} Hymns
              </p>
            </motion.div>
          )}
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 3: EDITORIAL NARRATIVE
          ═══════════════════════════════════════════════════════════════ */}
      <section className="relative py-32 overflow-hidden z-10">
        <div className="max-w-7xl mx-auto px-6">
          <EditorialReveal>
            <h2
              className="text-5xl md:text-7xl lg:text-[8rem] font-black leading-none tracking-tighter"
              style={{
                fontFamily: "Cinzel, serif",
                color: `${COLORS.stoneLight}e6`,
              }}
            >
              4,000 YEARS
            </h2>
          </EditorialReveal>

          <EditorialReveal delay={0.2}>
            <h2
              className="text-5xl md:text-7xl lg:text-[8rem] font-black leading-none tracking-tighter -mt-4"
              style={{
                fontFamily: "Cinzel, serif",
                color: `${COLORS.antiqueGold}cc`,
              }}
            >
              OF DATA.
            </h2>
          </EditorialReveal>

          <div className="h-32" />

          <EditorialReveal>
            <h2
              className="text-5xl md:text-7xl lg:text-[8rem] font-black leading-none tracking-tighter text-right"
              style={{
                fontFamily: "Cinzel, serif",
                color: `${COLORS.stoneLight}e6`,
              }}
            >
              DIGITIZED.
            </h2>
          </EditorialReveal>

          <div className="h-32" />

          <EditorialReveal delay={0.1}>
            <p
              className="text-xl md:text-2xl max-w-2xl leading-relaxed"
              style={{ color: COLORS.stoneMuted }}
            >
              The Rig Veda contains{" "}
              <span style={{ color: COLORS.antiqueGold }}>10,552 verses</span>{" "}
              across
              <span style={{ color: COLORS.antiqueGold }}> 1,028 hymns</span>,
              organized into
              <span style={{ color: COLORS.antiqueGold }}> 10 mandalas</span>.
              Each verse is a thread in humanity's oldest continuous
              intellectual tradition.
            </p>
          </EditorialReveal>

          <div className="h-24" />

          <EditorialReveal>
            <h2
              className="text-5xl md:text-7xl lg:text-[6rem] font-black leading-none tracking-tighter mix-blend-exclusion"
              style={{ fontFamily: "Cinzel, serif" }}
            >
              EXPLORE THE ORIGINS.
            </h2>
          </EditorialReveal>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 4: CALL TO ACTION
          ═══════════════════════════════════════════════════════════════ */}
      <section className="relative py-32 flex items-center justify-center z-10">
        <div className="text-center">
          <EditorialReveal>
            <p
              className="text-xs tracking-[0.5em] uppercase mb-8"
              style={{
                fontFamily: "JetBrains Mono, monospace",
                color: `${COLORS.antiqueGold}b3`,
              }}
            >
              Begin Your Journey
            </p>
          </EditorialReveal>

          <EditorialReveal delay={0.2}>
            <GlassRelicButton onClick={() => onEnterMandala(1)}>
              Enter the Vedas
            </GlassRelicButton>
          </EditorialReveal>

          <EditorialReveal delay={0.4}>
            <p
              className="text-sm mt-10"
              style={{ color: `${COLORS.stoneMuted}99` }}
            >
              Or select a Mandala from the constellation above
            </p>
          </EditorialReveal>
        </div>
      </section>

      {/* Footer */}
      <footer
        className="border-t py-12 z-10 relative"
        style={{ borderColor: `${COLORS.antiqueGold}15` }}
      >
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <Sparkles
              className="w-5 h-5"
              style={{ color: COLORS.antiqueGold }}
            />
            <span
              className="text-lg font-semibold tracking-tight"
              style={{ fontFamily: "Cinzel, serif", color: COLORS.stoneLight }}
            >
              VEDA
            </span>
          </div>

          <p
            className="text-sm"
            style={{
              fontFamily: "JetBrains Mono, monospace",
              color: `${COLORS.stoneMuted}80`,
            }}
          >
            The Code of the Cosmos
          </p>

          <p className="text-xs" style={{ color: `${COLORS.stoneMuted}60` }}>
            Built with reverence for ancient wisdom
          </p>
        </div>
      </footer>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// EDITORIAL REVEAL COMPONENT - Scroll-triggered text animation
// ═══════════════════════════════════════════════════════════════════════════
interface EditorialRevealProps {
  children: React.ReactNode;
  delay?: number;
}

const EditorialReveal: React.FC<EditorialRevealProps> = ({
  children,
  delay = 0,
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.8, delay, ease: [0.25, 0.1, 0.25, 1] }}
    >
      {children}
    </motion.div>
  );
};

export default RenaissanceLanding;
